import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Wallet, DoorOpen, MailCheck, MessageSquare, UtensilsCrossed,
  ChevronLeft, ChevronRight, CheckCircle2, ArrowRight, Sparkles, Bell, TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { MockNotes, MockPaiements, MockPortail, MockRapport, MockMessagerie, MockCantine } from "@/components/DemoMockups";

const STEPS = [
  {
    id: "notes", icon: GraduationCap, color: "#1E3A8A", title: "Suivez les notes en temps réel",
    text: "Dès qu'un enseignant saisit une note, vous la voyez sur votre téléphone. Moyennes, classements et bulletins PDF téléchargeables — plus besoin d'attendre la fin du trimestre.",
    Mock: MockNotes,
  },
  {
    id: "paiements", icon: Wallet, color: "#166534", title: "Payez la scolarité en 9 tranches",
    text: "Un plan de paiement automatique en 9 tranches, des reçus PDF officiels envoyés par email, et des rappels avant chaque échéance. Fini les files d'attente à la caisse.",
    Mock: MockPaiements,
  },
  {
    id: "portail", icon: DoorOpen, color: "#EA580C", title: "Soyez alerté à chaque entrée et sortie",
    text: "Votre enfant scanne son badge au portail de l'école : vous recevez instantanément une alerte. Vous savez toujours qu'il est bien arrivé — et bien reparti.",
    Mock: MockPortail,
  },
  {
    id: "rapport", icon: MailCheck, color: "#7C3AED", title: "Recevez un rapport chaque semaine",
    text: "Tous les samedis, un rapport clair dans votre boîte mail : présences, notes de la semaine, devoirs à venir et observations des enseignants.",
    Mock: MockRapport,
  },
  {
    id: "messagerie", icon: MessageSquare, color: "#0891B2", title: "Échangez directement avec l'école",
    text: "Messagerie intégrée avec l'administration et les enseignants, suivi de vos réclamations avec réponse garantie. Votre voix compte.",
    Mock: MockMessagerie,
  },
  {
    id: "cantine", icon: UtensilsCrossed, color: "#B45309", title: "Cantine, bibliothèque et bien plus",
    text: "Menus de la semaine, abonnement cantine, livres empruntés et badges de lecture de votre enfant... tout l'écosystème scolaire dans une seule application.",
    Mock: MockCantine,
  },
];

export default function Demo() {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <div className="bg-[#0F172A] min-h-screen text-white" data-testid="page-demo">
      {/* En-tête */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10">
        <div className="chip bg-white/10 border-white/20 text-[#FBBF24] mb-4">
          <Sparkles className="h-3 w-3" /><span>Visite guidée — 2 minutes</span>
        </div>
        <h1 className="mazid-headline text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
          Découvrez l&apos;expérience parent <span className="text-[#FBBF24]">Mazid School Connect</span>
        </h1>
        <p className="text-slate-300 mt-4 max-w-2xl text-base">
          Voici ce que vivent chaque jour les parents de nos élèves. Suivez la visite, puis rejoignez-nous.
        </p>
      </section>

      {/* Corps de la visite */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-10">
        {/* Barre de progression */}
        <div className="flex items-center gap-2 mb-8" data-testid="demo-progress">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === step ? "w-12" : "w-6 hover:opacity-80"}`}
              style={{ backgroundColor: i <= step ? current.color : "rgba(255,255,255,0.15)" }}
              aria-label={`Étape ${i + 1}`}
              data-testid={`demo-step-dot-${i}`}
            />
          ))}
          <span className="ml-auto text-xs text-slate-400 tabular-nums">Étape {step + 1} / {STEPS.length}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Texte de l'étape */}
          <div>
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 24 }}
                transition={{ duration: 0.3 }}
              >
                <div className="inline-flex p-3 rounded-2xl mb-5" style={{ backgroundColor: `${current.color}33` }}>
                  <current.icon className="h-8 w-8" style={{ color: current.color === "#1E3A8A" ? "#93C5FD" : current.color }} />
                </div>
                <h2 className="font-heading text-lg md:text-lg font-extrabold mb-3 text-2xl sm:text-3xl leading-tight" data-testid="demo-step-title">{current.title}</h2>
                <p className="text-slate-300 leading-relaxed">{current.text}</p>
              </motion.div>
            </AnimatePresence>

            <div className="flex items-center gap-3 mt-8">
              <Button
                variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 gap-1 disabled:opacity-30"
                data-testid="demo-prev"
              >
                <ChevronLeft className="h-4 w-4" /> Précédent
              </Button>
              {isLast ? (
                <Link to="/inscription">
                  <Button className="rounded-full bg-[#166534] hover:bg-[#14532d] text-white font-bold gap-2 px-6" data-testid="demo-final-cta">
                    Inscrire mon enfant <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              ) : (
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  className="rounded-full text-white font-bold gap-1 px-6"
                  style={{ backgroundColor: current.color }}
                  data-testid="demo-next"
                >
                  Suivant <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Maquette téléphone animée */}
          <div className="flex justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.id}
                initial={{ opacity: 0, y: 30, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.96 }}
                transition={{ duration: 0.35 }}
                className="w-[320px] sm:w-[360px] rounded-[2.2rem] border-[6px] border-slate-700 bg-white shadow-2xl overflow-hidden"
                data-testid="demo-phone-mockup"
              >
                <div className="bg-[#1E3A8A] px-4 py-3 flex items-center justify-between">
                  <div className="text-white text-xs font-bold">MAZID CONNECT</div>
                  <Bell className="h-3.5 w-3.5 text-[#FBBF24]" />
                </div>
                <div className="p-4 min-h-[380px] bg-slate-50">
                  <current.Mock />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-[#166534] py-14 mt-8">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <TrendingUp className="h-9 w-9 mx-auto mb-3 text-[#FBBF24]" />
          <h2 className="font-heading text-lg font-extrabold text-2xl mb-2">Rejoignez les centaines de familles qui nous font confiance</h2>
          <p className="text-green-100 text-sm max-w-xl mx-auto mb-6">
            96 % de réussite aux examens, un suivi digital complet et une équipe dévouée. L&apos;inscription en ligne prend moins de 5 minutes.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/inscription">
              <Button size="lg" className="rounded-full bg-[#FBBF24] hover:bg-amber-400 text-[#1E3A8A] font-extrabold px-8 gap-2" data-testid="demo-cta-inscription">
                Inscrire un enfant <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="rounded-full border-white/40 bg-transparent text-white hover:bg-white/10 font-bold px-8">
                Poser une question
              </Button>
            </Link>
          </div>
          <div className="flex items-center justify-center gap-4 mt-6 text-xs text-green-200">
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Établissement agréé</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Paiement en 9 tranches</span>
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Application gratuite</span>
          </div>
        </div>
      </section>
    </div>
  );
}
